import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { Storefront as StorefrontIcon } from '@mui/icons-material';
import ClaimForm from '../pro/ClaimForm';
import ClaimStatus from '../pro/ClaimStatus';
import { useMyClaim } from '../../hooks/pro/useMyClaim';
import { useOptionalAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

/**
 * "C'est votre commerce ?" — on a listing nobody maintains.
 *
 * An admin seeds the directory with listings before the businesses sign up.
 * When one of them finds its own here, this is how it asks for it instead of
 * writing a second one. Nothing shows on a listing that has an owner:
 * `claimable` says so, without saying who.
 *
 * A visitor is sent to sign up and brought back here; somebody signed in gets
 * the form in a dialog, and the state of their request afterwards.
 */
function ClaimPanel({ provider }) {
  const { isAuthenticated } = useOptionalAuth();
  const { pathname } = useLocation();
  const toast = useToast();
  const { claim, pending, loading, saving, error, fieldErrors, reset, send, withdraw } = useMyClaim({
    enabled: Boolean(isAuthenticated && provider.claimable),
  });
  const [open, setOpen] = useState(false);

  if (!provider.claimable || loading) {
    return null;
  }

  const isShop = 'COMMERCE' === provider.kind;
  const question = isShop ? 'C’est votre commerce ?' : 'C’est votre activité ?';
  const what = isShop ? 'votre commerce' : 'votre activité';
  const next = encodeURIComponent(pathname);
  const thisOne = claim?.provider?.slug === provider.slug;

  const close = () => {
    reset();
    setOpen(false);
  };

  const submit = async (values) => {
    if (await send(provider, values)) {
      setOpen(false);
      toast.success('Votre demande est envoyée. Un modérateur va la vérifier.');
    }
  };

  let body;

  if (!isAuthenticated) {
    body = (
      <>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Cette fiche a été créée par l’équipe du site. Si elle représente {what}, demandez sa
          gestion : vous pourrez ensuite la compléter et la mettre à jour vous-même.
        </Typography>
        <Button component={RouterLink} to={`/register?next=${next}`} variant="outlined" fullWidth>
          Créer un compte pour la reprendre
        </Button>
        <Typography variant="body2" sx={{ mt: 1.5, textAlign: 'center' }}>
          <Link component={RouterLink} to={`/login?next=${next}`}>
            J’ai déjà un compte
          </Link>
        </Typography>
      </>
    );
  } else if (pending && thisOne) {
    body = <ClaimStatus claim={claim} onWithdraw={withdraw} saving={saving} />;
  } else if (pending) {
    // One request at a time: the other has to be withdrawn first, from the
    // listing it was made on.
    body = (
      <Typography variant="body2">
        Vous avez déjà demandé la fiche{' '}
        <Link component={RouterLink} to={`/prestataires/${claim.provider.slug}`}>
          {claim.provider.name}
        </Link>
        . Retirez cette demande pour en faire une autre.
      </Typography>
    );
  } else {
    body = (
      <>
        {thisOne && <ClaimStatus claim={claim} sx={{ mb: 2 }} />}
        <Typography variant="body2" sx={{ mb: 2 }}>
          Cette fiche a été créée par l’équipe du site. Si elle représente {what}, demandez sa
          gestion : vous pourrez ensuite la compléter et la mettre à jour vous-même.
        </Typography>
        <Button variant="outlined" fullWidth onClick={() => setOpen(true)}>
          Demander cette fiche
        </Button>
      </>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <StorefrontIcon sx={{ color: 'text.disabled' }} />
        <Typography variant="h3" component="h2">
          {question}
        </Typography>
      </Box>

      {body}

      {error && !open && (
        <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
          {error}
        </Typography>
      )}

      <Dialog open={open} onClose={saving ? undefined : close} fullWidth maxWidth="sm">
        <DialogTitle>Demander la fiche « {provider.name} »</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <ClaimForm
            provider={provider}
            onSubmit={submit}
            onCancel={close}
            saving={saving}
            error={error}
            fieldErrors={fieldErrors}
          />
        </DialogContent>
      </Dialog>
    </Paper>
  );
}

export default ClaimPanel;
